import { Model } from 'app/model/backendmodel';
import { Inject, Injectable, Injector, Optional } from '@angular/core';
import {
    akvSpecValue,
    concatParameterSyntax,
    containerSasToken,
    containerUriParam,
    IArmTemplate,
    IArmTemplateResource,
    IDependency,
    integrationRuntimeParamPrefix,
    parameterSyntax,
    TemplateBreakingOptions,
    variableSyntax
} from 'app/interfaces/IArmTemplate';
import * as Constants from 'app/services/globals';
import { Constants as SharedConsts } from 'app/shared/shared-constants';

import {
    AdapterContext,
    ARMTemplateIdentityProvider,
    Metamodel,
    ModelConstants,
    MODEL_SERVICE_LIST_TIMEOUT,
    ModelUtilties,
    Named,
    stringsAreEqualCaseInsensitive,
    Walker,
    A365Named,
    Database,
    DatabaseEntity
} from 'shell-models-lib';
import { TumblingWindowTriggerProperties } from '../trigger/tumbling-window-trigger-properties';
import { ArmTemplateUtils, factoryNameParam } from './publisher/armTemplate.utils';
import { PublishConfigService } from './publisher/publish-config.service';
import { IPublishableResource, IPublishConfig } from './publisher/publisher.datatypes';
import { convertObjectToString, format } from 'app/shared/utils/string.util';
import { PrimaryResourceUrlInformationService } from 'app/services/primary-resource-url-information.service';
import { resolveResourceType } from 'app/shared/utils/model.concepts.utils';
import { ModelService } from './model.service';
import { Dictionary } from 'lodash';
import { PublisherUtils } from './publisher/publisher.utils';
import { LoggerService } from 'app/logger/logger.service';
import { ResourcePublisherUtils } from 'app/services/resource-publisher-utils';
import { IFile } from 'app/interfaces/IFile';
import { DataFlowCleanupService } from 'app/services/dataflow-cleanup.service';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { DataFlow } from '../dataflow/dataflow';
import { ARM_TEMPLATE_OPTIONS, IARMTemplateOptions } from 'cmd-api/services/local-resource-provider.tokens';
import { Trigger } from '../trigger/trigger';
import { IFrameService } from 'app/services/iframe.service';
import { IdentityService } from 'app/model/services/identity.service';
import { IResourceContext, RESOURCE_CONTEXT_DATA } from 'app/multi-contexts/resource-context.typings';

@Injectable({
    providedIn: 'root'
})
export class ArmTemplateGeneratorService {
    public static readonly _linkedTemplateFolder = 'linkedTemplates';
    public static readonly _armTemplateLinkedFileName = `${ArmTemplateGeneratorService._linkedTemplateFolder}/ArmTemplate_{0}.json`;
    public static readonly _armTemplateLinkedParametersFileName = `${ArmTemplateGeneratorService._linkedTemplateFolder}/ArmTemplateParameters_{0}.json`;
    public static readonly _armTemplateFileName = __IS_A365__
        ? 'TemplateForWorkspace.json'
        : 'ARMTemplateForFactory.json';
    public static readonly _armTemplateParametersFileName = __IS_A365__
        ? 'TemplateParametersForWorkspace.json'
        : 'ARMTemplateParametersForFactory.json';
    protected static readonly className = 'ArmTemplateGeneratorService';
    private static readonly maxCharsInTemplate: number = 800000;
    private static readonly factoryResourcePrefix: string = __IS_A365__ ? 'workspace' : 'dataFactory';
    private static readonly globalParamsResourcePrefix: string = 'default';

    public _exportingTemplate$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public exportingTemplate$: Observable<boolean>;
    private readonly _isTrident = __IS_FABRIC_DPE__;
    private readonly _fullResourceType = __IS_A365__
        ? Constants.WORKSPACE_FULLRESOURCE_TYPE
        : Constants.FACTORY_FULLRESOURCE_TYPE;
    private readonly _nameParam = ArmTemplateUtils.nameParam;
    private readonly _idVariable = ArmTemplateUtils.idVariable;
    private _maxResourcesAllowedForLinking: number = 20;
    private _dataFlowCleanup: DataFlow[] = [];
    private _cleanDataFlow: boolean = false;
    private _isExportingTridentPipeline: boolean = false;

    private get _publishConfig(): IPublishConfig {
        return this._publishConfigService.publishConfig;
    }

    constructor(
        @Inject(RESOURCE_CONTEXT_DATA) private _contextData: IResourceContext,
        private readonly _modelService: ModelService,
        private readonly _publishConfigService: PublishConfigService,
        private readonly _appUrlService: PrimaryResourceUrlInformationService,
        private readonly _dataFlowCleanUpService: DataFlowCleanupService,
        private readonly _logger: LoggerService,
        @Optional() @Inject(ARM_TEMPLATE_OPTIONS) private readonly _armTemplateOptions: IARMTemplateOptions,
        private readonly _identityService: IdentityService,
        private readonly _injector: Injector
    ) {
        this.exportingTemplate$ = this._exportingTemplate$.asObservable();
        this._exportingTemplate$.next(false);
    }

    public setMaxResourcesForLinkedTemplates(maxResources: number): void {
        this._maxResourcesAllowedForLinking = maxResources;
    }

    public setIsExportingTridentPipeline(isExportingPipeline: boolean): void {
        this._isExportingTridentPipeline = isExportingPipeline;
    }

    public getIsExportingTridentPipeline(): boolean {
        return this._isExportingTridentPipeline;
    }

    public async getAllArmTemplates(parameterDefinition?: object): Promise<IFile[]> {
        const armTemplateFiles: IFile[] = [];
        try {
            this._logger.log(ArmTemplateGeneratorService.className, 'Generating templates');
            await this._parseDataFlowIfNeeded();

            const fullArmTemplate = await this.generateMainArmTemplate(parameterDefinition);
            armTemplateFiles.push(...fullArmTemplate);
            if (!__IS_A365__) {
                const linkedTemplates = await this.generateLinkedTemplates(parameterDefinition);
                armTemplateFiles.push(...linkedTemplates);
            }
        } catch (error) {
            const armExportError = error as Error;
            if (armExportError && armExportError.message === stringResources.ARMTemplateGenerationFailureMessage) {
                this._logger.log(
                    ArmTemplateGeneratorService.className,
                    `Failed to export template due to some bad resource. Error: ${convertObjectToString(error)}`
                );
            }
            this._logger.log(
                ArmTemplateGeneratorService.className,
                `Failed to export template. Error: ${convertObjectToString(error)}`
            );
            throw error;
        } finally {
            this._cleanDataFlowIfNeeded();
        }

        return armTemplateFiles;
    }

    public async generateMainArmTemplate(parameterDefinition?: object): Promise<IFile[]> {
        const armTemplate: IFile[] = [];
        parameterDefinition =
            parameterDefinition || (await ArmTemplateUtils.getArmTemplateParameterDefinition(this._contextData));
        this._logger.log(ArmTemplateGeneratorService.className, 'Generating full template');
        const mainArmTemplate = await this.getResourceArmTemplateParameterized(null, null, parameterDefinition);
        this._logger.log(ArmTemplateGeneratorService.className, 'Generating full template parameters file');
        const fullArmTemplateParameters = this.getAllResourceArmTemplateParameters(mainArmTemplate);
        this._addToPendingFile(armTemplate, mainArmTemplate, ArmTemplateGeneratorService._armTemplateFileName);
        this._addToPendingFile(
            armTemplate,
            fullArmTemplateParameters,
            ArmTemplateGeneratorService._armTemplateParametersFileName
        );
        return armTemplate;
    }

    public async generateLinkedTemplates(parameterDefinition?: object): Promise<IFile[]> {
        this._logger.log(ArmTemplateGeneratorService.className, 'Generating Linked Templates');
        const linkedResources: IArmTemplateResource[] = [];
        const linkedTemplates: IFile[] = [];
        let countResources = 0;
        let countTemplates = 0;

        let orderedResource = await PublisherUtils.getResourcesOrdered(
            { contextData: this._contextData, ...this._modelService.modelAttributeContext },
            undefined,
            this._publishConfig.includeFactoryTemplate,
            this._publishConfig.includeFactoryTemplateWithCmkSettings,
            this._publishConfig.includeGlobalParamsTemplate
        );
        parameterDefinition =
            parameterDefinition || (await ArmTemplateUtils.getArmTemplateParameterDefinition(this._contextData));

        if (this._armTemplateOptions?.excludeTriggers) {
            orderedResource = this._excludeTriggersFromResources(orderedResource);
        }

        // process all resources into linked templates
        while (countResources < orderedResource.length) {
            const armTemplate = await this.getResourceArmTemplateParameterized(
                orderedResource.slice(countResources, countResources + this._maxResourcesAllowedForLinking),
                true,
                parameterDefinition
            );
            const armTemplateParameters = this.getAllResourceArmTemplateParameters(armTemplate, true);
            countResources += armTemplate.resources.length;

            this._addToPendingFile(
                linkedTemplates,
                armTemplate,
                format(ArmTemplateGeneratorService._armTemplateLinkedFileName, countTemplates.toString())
            );
            // linked param file not supported (can't pass values currently)
            // this._addToPendingArmTemplates(armTemplateParameters, format(this._armTemplateLinkedParametersFileName, countTemplates.toString()), false, false);

            // add a linkedResource for this template
            linkedResources.push(
                ResourcePublisherUtils.getDeploymentResource(
                    'ArmTemplate',
                    countTemplates,
                    armTemplateParameters,
                    this._appUrlService.service.resourceName
                )
            );

            countTemplates++;
        }

        // build the master template and master parameters if needed
        if (countTemplates) {
            this._logger.log(ArmTemplateGeneratorService.className, 'Building master arm template and parameters file');
            const armTemplate = await this.getResourceArmTemplateParameterized(null, null, parameterDefinition);

            // add params for URI and Sas token
            armTemplate.parameters[containerUriParam] = {
                type: 'string',
                metadata: stringResources.ContainerUri
            };
            armTemplate.parameters[containerSasToken] = {
                type: 'string',
                metadata: stringResources.ContainerSasToken
            };

            const armTemplateParameters = this.getAllResourceArmTemplateParameters(armTemplate);

            // set resources to linked resources
            armTemplate.resources = linkedResources.slice();

            this._addToPendingFile(
                linkedTemplates,
                armTemplate,
                format(ArmTemplateGeneratorService._armTemplateLinkedFileName, 'master')
            );
            this._addToPendingFile(
                linkedTemplates,
                armTemplateParameters,
                format(ArmTemplateGeneratorService._armTemplateLinkedParametersFileName, 'master')
            );
        }
        return linkedTemplates;
    }

    public getAllResourceArmTemplateParameters(template: IArmTemplate, forLinkedTemplate?: boolean): IArmTemplate {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const parameters: Dictionary<any> = {};
        for (const param in template.parameters) {
            if (forLinkedTemplate) {
                parameters[param] = {
                    value: `[parameters('${param}')]`
                };
            } else {
                // In the full arm template, the resource dependencies are configured as dependencies based on resource ids
                // via dependsOn attribute.
                // This works fine, except when a reference attribute is setup for custom parameterization
                // (for ex. LS attribute connectVia that points to IR)
                // When this is the case, the reference attribute and the dependsOn points to its custom parameter for IR
                // The default value of this attribute is set to the default IR (resource id), however the default value
                // of this parameter refers to the the default IR parameter (parameter reference)
                // This is invalid as a parameter value cant refer to another parameter in the ARM template
                // The fix is to resolve any parameter references in the parameter default values

                // Note that in the child linked templates, the IR parameter may not even exist in the template itself
                // But thats ok, since the master template and its parameter file are fixed to not have parameter references

                const defaultValue = template.parameters[param].defaultValue;
                if (defaultValue && typeof defaultValue === 'string' && defaultValue.indexOf(`[parameters('`) === 0) {
                    template.parameters[param].defaultValue =
                        template.parameters[defaultValue.replace(`[parameters('`, '').replace(`')]`, '')].defaultValue;
                }

                parameters[param] = {
                    value:
                        template.parameters[param].defaultValue === undefined
                            ? ''
                            : template.parameters[param].defaultValue
                };
            }
        }
        return {
            $schema: 'https://schema.management.azure.com/schemas/2015-01-01/deploymentParameters.json#',
            contentVersion: '1.0.0.0',
            parameters
        };
    }

    // TODO: This parameters should be done in a generic way, maybe with an annotation in the model.
    public async getResourceArmTemplateParameterized(
        namedList?: Named[],
        breakingTemplates?: boolean,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        parameterDefinition?: any,
        publishConfig?: IPublishConfig
    ): Promise<IArmTemplate> {
        if (!breakingTemplates) {
            breakingTemplates = false;
        }

        if (!publishConfig) {
            publishConfig = this._publishConfig;
        }

        if (!namedList) {
            let resources: Named[] = [];
            if (Constants.GlobalVariables.enableOrderedArmTemplateGeneration) {
                resources = await PublisherUtils.getResourcesOrdered(
                    { contextData: this._contextData, ...this._modelService.modelAttributeContext },
                    undefined,
                    publishConfig.includeFactoryTemplate,
                    publishConfig.includeFactoryTemplateWithCmkSettings,
                    publishConfig.includeGlobalParamsTemplate
                );
            } else {
                resources = await PublisherUtils.getPublishableNameds(this._contextData);
                // Add global parameters to new location only if new flag is turned on (will ignore old flags if so)
                if (publishConfig.includeGlobalParamsTemplate) {
                    const globalParamsForARM = await PublisherUtils.createGlobalParametersForArmTemplate(this._contextData);
                    resources.push(globalParamsForARM);
                } else if (publishConfig.includeFactoryTemplate || publishConfig.includeFactoryTemplateWithCmkSettings) {
                    const factoryClone = await PublisherUtils.getFactoryCloneForArmTemplate(this._contextData, {
                        includeFactoryWithGlobalParameters: publishConfig.includeFactoryTemplate,
                        includeFactoryWithCmkSettings: publishConfig.includeFactoryTemplateWithCmkSettings
                    });
                    resources.push(factoryClone);
                }
            }
            if (this._armTemplateOptions?.excludeTriggers) {
                resources = this._excludeTriggersFromResources(resources);
            }
            namedList = await this._modelService.fetchAll(resources, true);
        }

        // For live only resources in Synapse, they should not be included in ARM template since they don't support CI/CD
        namedList = namedList.filter(
            (named) => !(named instanceof A365Named && (named.liveOnly || named.excludedInArmTemplate))
        );

        const breakingOptions = breakingTemplates
            ? TemplateBreakingOptions.LinkedTemplateBased
            : TemplateBreakingOptions.NoBreaking;
        const template = await this.getResourceArmTemplate(namedList, breakingOptions);
        template.parameters = template.parameters || {};

        this._addSecureStringParameters(template);
        await this.addPredefinedParameters(template, publishConfig, parameterDefinition);

        return template;
    }

    // this one only used by solution gallery currently
    public async getResourceArmTemplateForGallery(namedList: Named[]): Promise<IArmTemplate> {
        const breakingOptions = TemplateBreakingOptions.NoBreaking;
        const template = await this.getResourceArmTemplate(namedList, breakingOptions, true);
        template.parameters = template.parameters || {};
        if (template.parameters[factoryNameParam]) {
            delete template.parameters[factoryNameParam].defaultValue;
        }

        this._addSecureStringParameters(template);

        return template;
    }

    public async getResourceArmTemplate(
        namedList: Named[],
        breakingOptions: TemplateBreakingOptions,
        parseArm?: boolean
    ): Promise<IArmTemplate> {
        const promises: Array<Promise<IPublishableResource>> = [];
        for (const named of namedList) {
            if (!named.isOk) {
                throw new Error(
                    `${stringResources.ARMTemplateGenerationFailureMessage}, resource name: ${named?.name}`
                );
            }

            // While publishing retains the secrets
            const context: AdapterContext =
                breakingOptions === TemplateBreakingOptions.PublishBased
                    ? undefined
                    : AdapterContext.newAdapterContext({
                          contextData: this._contextData,
                          identityProvider: ARMTemplateIdentityProvider.instance,
                          removeEncryptedCredentials: true
                      });

            if (this._isTrident && context != null) {
                this._injector
                    .get<IFrameService>(IFrameService)
                    .updateAdapterContextByIframeCustomerProfile(context, this._contextData.contextType);
            }
            promises.push(this._convertToARMResourceForPublishing(named, namedList, true, context));
        }
        const publishableResources = await Promise.all(promises);
        const resources: IPublishableResource[] = [].concat.apply([], publishableResources);

        return await this._getArmTemplateForResources(resources, breakingOptions, parseArm);
    }

    private async _parseDataFlowIfNeeded(): Promise<void> {
        this._logger.log(ArmTemplateGeneratorService.className, 'Checking if dataflows need parsing.');
        const namedList =
            ((await this._modelService
                .list()
                .pipe(timeout({ each: 5, with: () => from([]) }))
                .toPromise()) as Named[]) || [];
        await this._modelService.fetchAll(namedList);

        for (const entity of namedList) {
            if (entity instanceof DataFlow) {
                if (!entity.hasParsed) {
                    this._cleanDataFlow = true;
                    await entity.setupCache(this._contextData);
                    this._dataFlowCleanup.push(entity);
                } else {
                    entity.updateCachedDataForTransformations();
                }
            }
        }
        this._logger.log(ArmTemplateGeneratorService.className, `Dataflows need parsing: ${this._cleanDataFlow}`);
    }

    private async _cleanDataFlowIfNeeded(): Promise<void> {
        // Only clean if we had to parse the dataflow, otherwise, let whatever service parsed it to clean it up
        if (this._cleanDataFlow) {
            this._logger.log(ArmTemplateGeneratorService.className, 'Cleaning dataflows.');
            for (const entity of this._dataFlowCleanup) {
                this._dataFlowCleanUpService.cleanUpDataFlow(entity);
            }
        }
    }

    /**
     * This method is called in the below cases :
     * 1. To publish to backend (from base.publisher)
     * 2. Export ARM templates (full, linked)
     * 3. Generate CICD ARM templates (linked, partial, delta, full)
     */
    private async _getArmTemplateForResources(
        publishableResources: IPublishableResource[],
        breakingOptions: TemplateBreakingOptions,
        parseArm?: boolean
    ): Promise<IArmTemplate> {
        const resources = publishableResources.map((r) => this._convertToArmResource(r, breakingOptions));

        const template = await this._getArmTemplateForResourcesBase(resources, breakingOptions, parseArm);
        if (!this._isTrident) {
            template.parameters[this._nameParam] = {
                type: 'string',
                metadata: __IS_A365__ ? stringResources.WorkspaceName : stringResources.DataFactoryName,
                defaultValue: this._appUrlService.service.resourceName
            };
            template.variables[this._idVariable] = `[${format(
                concatParameterSyntax,
                `'${this._fullResourceType}/', ${format(parameterSyntax, this._nameParam)}`
            )}]`;
        }
        if (this._publishConfig.irNameParameterization) {
            for (const resource of publishableResources) {
                if (stringsAreEqualCaseInsensitive(resource.conceptType, 'integrationRuntime')) {
                    const irNameParameter = integrationRuntimeParamPrefix + resource.resourceName;
                    template.parameters[irNameParameter] = {
                        type: 'string',
                        metadata: stringResources.IRName,
                        defaultValue: resource.resourceName
                    };
                }
            }
        }
        return template;
    }

    private async _getArmTemplateForResourcesBase(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        resources: any[],
        breakingOptions: TemplateBreakingOptions,
        parseArm?: boolean
    ): Promise<IArmTemplate> {
        let currentResourcesInTemplate = 0;
        let templateLength = 0;
        const template: IArmTemplate = {
            // eslint-disable-next-line
            $schema: 'http://schema.management.azure.com/schemas/2015-01-01/deploymentTemplate.json#',
            contentVersion: '1.0.0.0',
            parameters: {},
            variables: {},
            resources
        };

        let maxResourcesAllowed: number = Number.MAX_SAFE_INTEGER;
        if (breakingOptions === TemplateBreakingOptions.LinkedTemplateBased) {
            maxResourcesAllowed = this._maxResourcesAllowedForLinking;
        } else if (breakingOptions === TemplateBreakingOptions.PublishBased) {
            maxResourcesAllowed = PublisherUtils.maxResourcesAllowedForPublishing;
        }

        // make params for values with brackets so the ARM template works properly
        for (const resource of template.resources) {
            const stringResource = this._getStringResource(resource, parseArm);
            const jsonResource = JSON.parse(stringResource);

            // break - if template is too big or max resources is reached
            if (
                breakingOptions !== TemplateBreakingOptions.NoBreaking &&
                (templateLength + stringResource.length > ArmTemplateGeneratorService.maxCharsInTemplate ||
                    currentResourcesInTemplate >= maxResourcesAllowed)
            ) {
                // clear the remainder of resources from the template
                template.resources = template.resources.slice(0, currentResourcesInTemplate);
                if (currentResourcesInTemplate === 0) {
                    const errorMsg =
                        currentResourcesInTemplate >= maxResourcesAllowed
                            ? breakingOptions === TemplateBreakingOptions.LinkedTemplateBased
                                ? format(
                                      stringResources.ARMTemplateResourceCountExceededLinked,
                                      resource.name || '',
                                      maxResourcesAllowed.toString()
                                  )
                                : format(
                                      stringResources.ARMTemplateResourceCountExceededPublished,
                                      resource.name || '',
                                      maxResourcesAllowed.toString()
                                  )
                            : format(
                                  stringResources.ARMTemplateCharCountExceeded,
                                  resource.name || '',
                                  ArmTemplateGeneratorService.maxCharsInTemplate.toString()
                              );
                    throw new Error(errorMsg);
                }
                break;
            }

            templateLength += stringResource.length;
            template.resources[currentResourcesInTemplate++] = jsonResource;
        }
        return template;
    }

    private _getStringResource(resource: IArmTemplateResource, parseArm?: boolean): string {
        return JSON.stringify(resource, (_key, value) => {
            if (
                typeof value === 'string' &&
                value.startsWith('[') &&
                value.endsWith(']') &&
                !(
                    value.startsWith('[concat(') ||
                    value.startsWith('[parameters(') ||
                    value.startsWith('[variables(') ||
                    value.startsWith('[resourceId(')
                )
            ) {
                if (__IS_A365__ && !parseArm) {
                    return value; // Synapse doesn't use ARM templates, and Synapse template doesn't need escape sequence.
                }
                return '[' + value; // Recommended ARM Escape sequence for values starting with '[' is to replace it with '[['
            }

            // Check if it is an object (null and array are typed as objects so exclude them)
            if (value != null && typeof value === 'object' && !Array.isArray(value)) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const replacement: any = {};
                Object.keys(value).forEach((k) => {
                    if (k.startsWith('[') && k.endsWith(']')) {
                        replacement['[' + k] = value[k];
                    } else {
                        replacement[k] = value[k];
                    }
                });
                return replacement;
            }
            return value;
        });
    }

    private _convertToArmResource(
        publishableResource: IPublishableResource,
        breakingOptions: TemplateBreakingOptions
    ): IArmTemplateResource {
        // While exporting ARM template, handle parameterization of integrationRuntime, including linkedService references/dependencies
        const isFactory =
            publishableResource.conceptType === 'factory' || publishableResource.conceptType === 'workspace';
        let resourceName = publishableResource.resourceName;
        let isNameParameterized = false;
        if (this._publishConfig.irNameParameterization && breakingOptions !== TemplateBreakingOptions.PublishBased) {
            if (stringsAreEqualCaseInsensitive(publishableResource.conceptType, 'integrationRuntime')) {
                const irNameParameter = integrationRuntimeParamPrefix + publishableResource.resourceName;
                resourceName = `${format(parameterSyntax, irNameParameter)}`;
                isNameParameterized = true;
            }

            if (stringsAreEqualCaseInsensitive(publishableResource.conceptType, 'linkedService')) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const connectVia = (publishableResource.resourceProperties as any)
                    .connectVia as Model.IntegrationRuntimeReference;
                if (connectVia) {
                    const irNameParameter = integrationRuntimeParamPrefix + connectVia.referenceName;
                    connectVia.referenceName = `[${format(parameterSyntax, irNameParameter)}]`;
                }
            }
        }

        let subResourceType = isFactory
            ? ''
            : publishableResource.conceptType === ModelConstants.managedPrivateEndpoint
            ? `/${ModelConstants.managedVirtualNetwork}s/${ModelConstants.managedPrivateEndpoint}s`
            : publishableResource.conceptType === ModelConstants.globalparameters
            ? `/${ModelConstants.globalparameters}`
            : `/${publishableResource.conceptType}s`;
        const name = this._isTrident
            ? resourceName
            : isFactory
            ? `[${format(parameterSyntax, this._nameParam)}]`
            : isNameParameterized
            ? `[${format(concatParameterSyntax, `${format(parameterSyntax, this._nameParam)}, '/', ${resourceName}`)}]`
            : `[${format(concatParameterSyntax, `${format(parameterSyntax, this._nameParam)}, '/${resourceName}'`)}]`;

        subResourceType = subResourceType === '/airflowEntitys' ? '/airflowEntities' : subResourceType;
        return {
            name,
            type: this._isTrident
                ? `${publishableResource.conceptType}s`
                : `${this._fullResourceType}${subResourceType}`,
            apiVersion: __IS_A365__ ? Constants.WORKSPACE_APIVERSION : Constants.GlobalVariables.dataFactoryVersion,
            properties: publishableResource.resourceProperties,
            dependsOn: this._getDependencies(
                publishableResource.dependencies,
                publishableResource.resourceName,
                publishableResource.resourceType,
                breakingOptions,
                isFactory
            ),
            location: publishableResource.location,
            ...(isFactory && publishableResource.factoryIdentity && { identity: publishableResource.factoryIdentity })
        };
    }

    private _getDependencies(
        dependencies: IDependency[],
        parentResourceName: string,
        parentResourceType: string,
        breakingOptions: TemplateBreakingOptions,
        isFactory: boolean
    ): string[] {
        const decoratedDependencies = [];
        if (
            breakingOptions === TemplateBreakingOptions.NoBreaking &&
            !isFactory &&
            this._publishConfig.includeFactoryTemplate
        ) {
            decoratedDependencies.push(`[${format(variableSyntax, this._idVariable)}]`);
        }
        for (const dependency of dependencies) {
            if (this._isExportingTridentPipeline && this._isTrident && dependency.conceptType === 'pipeline' && parentResourceType === 'pipeline') continue;
            if (
                dependency.resourceName !== parentResourceName ||
                parentResourceType !== TumblingWindowTriggerProperties.MODEL.name
            ) {
                let dependencyName = `[${format(
                    concatParameterSyntax,
                    `${format(variableSyntax, this._idVariable)}, '/${dependency.conceptType}s/${
                        dependency.resourceName
                    }'`
                )}]`;
                if (this._publishConfig.irNameParameterization) {
                    if (stringsAreEqualCaseInsensitive(dependency.conceptType, 'integrationRuntime')) {
                        const irNameParameter = integrationRuntimeParamPrefix + dependency.resourceName;
                        // eslint-disable-next-line max-len
                        dependencyName = `[${format(
                            concatParameterSyntax,
                            `${format(variableSyntax, this._idVariable)}, '/${dependency.conceptType}s/', ${format(
                                parameterSyntax,
                                irNameParameter
                            )}`
                        )}]`;
                    }
                }
                decoratedDependencies.push(dependencyName);
            }
        }

        return decoratedDependencies;
    }

    private parameterizeResource(
        rootResourceName: string,
        resourceName: string,
        resourceIndex: number,
        resource: IArmTemplateResource,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        definition: any,
        template: IArmTemplate
    ): void {
        // Follow the object model on both sides
        // --- setting a and array in the definition file indicates that the matching property in the resource will be an array and should iterate through  all its objects using
        //     the definition defined in the first object of the definition's array (second object is optional and is the path the get the name for each iteration)
        // --- setting property name to '*' means that we should use that to match all properties at that level (except the ones explicitly defined)
        // --- setting the value of a property as string means that property should be a parameter, it should be in the following format '<action>:<name>:<stype>'
        //     action can be
        //       '=' (keep current value as default value for the parameter)
        //       '-' (do not keep default vaule)
        //       '|'
        //         if it's AKV, do '=' for the 'secretName' in it
        //         if it has already been handled in addSecureStringParameters, update the property to ensure the property itself is string instead of SecureString
        //         otherwise, make the property itself string, parameter name/type/default value same as '-', parameter metadata same as addSecureStringParameters
        //     if name is left blank it will take the name of the property, if type is left blank it will be string.
        //     if there is '-' at the beginning of the name, the full ARM parameter name will be shorten, in term '<objectName>_<propertyName>'
        //       for example, 'AzureStorage1_properties_typeProperties_connectionString' will be changed to 'AzureStorage1_connectionString'
        //     stype is the type of ARM parameter
        for (const property in resource) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const value = (resource as any)[property];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const specValue = (definition as any)[property] || (definition as any)['*'];
            if (!specValue) {
                continue;
            }
            if (typeof specValue === 'string' && specValue.startsWith('|') && value.type === 'AzureKeyVaultSecret') {
                this.parameterizeResource(
                    rootResourceName,
                    `${resourceName}_${property}`,
                    resourceIndex,
                    value,
                    akvSpecValue,
                    template
                );
            } else if (
                typeof specValue === 'string' &&
                (specValue.startsWith('=') || specValue.startsWith('-') || specValue.startsWith('|'))
            ) {
                // parameter
                const parts = specValue.split(':');
                const isFactoryFloatCase =
                    (rootResourceName === ArmTemplateGeneratorService.factoryResourcePrefix ||
                        rootResourceName === ArmTemplateGeneratorService.globalParamsResourcePrefix) &&
                    resource.type === 'float';
                let propertyName = parts.length > 1 ? parts[1] : property;
                let propertyResourceName = resourceName;
                if (parts.length > 1 && parts[1].startsWith('-')) {
                    // Shorten parameter name
                    propertyName = propertyName.substring(1);
                    propertyResourceName = rootResourceName;
                }
                const paramName = propertyName ? `${propertyResourceName}_${propertyName}` : propertyResourceName;
                let defaultValue = value;

                if (!specValue.startsWith('|') && this.isPropertyParameterizableObject(value)) {
                    defaultValue = value.value;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (resource as any)[property].value = `[${format(parameterSyntax, paramName)}]`;
                } else {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (resource as any)[property] = isFactoryFloatCase
                        ? `[float(${format(parameterSyntax, paramName)})]`
                        : `[${format(parameterSyntax, paramName)}]`;
                }

                if (property === 'referenceName') {
                    // If a reference is parameterized, the resource dependencies in the ARM template must also be adjusted
                    // If the value is already parameterized, value will be [parameters('paramName')]
                    // Else by default it will be IRName

                    // Remove the begining and trailing '[' and ']'
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const parameterString = (resource as any)[property];
                    if (__IS_A365__ === false || typeof parameterString === 'string') {
                        let parameterStringToSet = parameterString.substring(1, parameterString.length - 1); // for ex: parameters('paramName')
                        let parameterStringToReplace: string;

                        // eslint-disable-next-line
                        if (value.indexOf('parameters(') !== -1) {
                            // value will be [parameters('paramName')], remove the brackets
                            parameterStringToReplace = value.substring(1, value.length - 1); // for ex: parameters('paramName')
                            // dependsOn entry would be: "[concat(variables('factoryId'), '/integrationRuntimes/', parameters('integrationRuntime3'))]"
                        } else {
                            // value will be integrationRunTime3.
                            parameterStringToReplace = `${value}\'`;
                            parameterStringToSet = `', ${parameterStringToSet}`;
                            // for ex. Search for integrationRunTime3' and replace with "' parameters('paramName')"
                            // dependsOn entry would be "[concat(variables('factoryId'), '/integrationRuntimes/integrationRuntime3')]"
                        }

                        const depArray = template.resources[resourceIndex].dependsOn;
                        for (let index = 0; index < depArray.length; index++) {
                            if (depArray[index].indexOf(parameterStringToReplace) !== -1) {
                                // depArray[index] would be "[concat(variables('factoryId'), '/integrationRuntimes/', parameters('integrationRuntime_IR-Intranet'))]"
                                depArray[index] = depArray[index].replace(
                                    parameterStringToReplace,
                                    parameterStringToSet
                                );
                            }
                        }
                    }
                }

                const paramType = isFactoryFloatCase
                    ? 'string'
                    : parts.length === 3
                    ? parts[2]
                    : this.getArmTemplateType(defaultValue);
                const originalValue = template.parameters[paramName]?.defaultValue;
                template.parameters[paramName] = {
                    type: paramType
                };
                if (specValue.startsWith('=')) {
                    template.parameters[paramName].defaultValue = isFactoryFloatCase ? `${defaultValue}` : defaultValue;
                } else if (specValue.startsWith('|')) {
                    // Same as SecureString
                    template.parameters[paramName].metadata = format(
                        stringResources.SecureStringParameter,
                        propertyName,
                        rootResourceName
                    );
                    template.parameters[paramName].defaultValue = originalValue;
                }
            } else if (Array.isArray(specValue) && specValue.length > 0) {
                // firt value of the definition's array is the definition for each object in the array
                const tempSpecValue = specValue[0];
                // second value is the property where to get the parameter name
                const paramNamePath = specValue[1] as string;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                value.forEach((val: any, index: number) => {
                    const paths = paramNamePath ? paramNamePath.split('.') : [];
                    let paramName = val;
                    paths.forEach((name) => {
                        paramName = paramName[name];
                    });
                    paramName = (typeof paramName === 'string' ? paramName : index.toString()) as string;
                    this.parameterizeResource(
                        rootResourceName,
                        `${resourceName}_${paramName}`,
                        resourceIndex,
                        val,
                        tempSpecValue,
                        template
                    );
                });
            } else if (typeof specValue === 'object') {
                this.parameterizeResource(
                    rootResourceName,
                    `${resourceName}_${property}`,
                    resourceIndex,
                    value,
                    specValue,
                    template
                );
            }
        }
    }

    private async addPredefinedParameters(
        template: IArmTemplate,
        publishConfig: IPublishConfig,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        parameterDefinition?: any
    ): Promise<void> {
        const resources = template.resources;

        if (!parameterDefinition) {
            parameterDefinition = await ArmTemplateUtils.getArmTemplateParameterDefinition(this._contextData);
        }

        for (let index: number = 0; index < resources.length; ++index) {
            const resource = resources[index];
            let definition = parameterDefinition[resource.type];
            if (
                definition &&
                (resource.type === 'Microsoft.DataFactory/factories/linkedServices' ||
                    resource.type === 'Microsoft.DataFactory/factories/datasets' ||
                    resource.type === 'Microsoft.Synapse/workspaces/linkedServices' ||
                    resource.type === 'Microsoft.Synapse/workspaces/datasets')
            ) {
                definition =
                    parameterDefinition[resource.type][resource.properties.type] ||
                    parameterDefinition[resource.type]['*'];
            }
            if (definition) {
                let name;
                if (
                    publishConfig.irNameParameterization &&
                    (resource.type === 'Microsoft.DataFactory/factories/integrationRuntimes' ||
                        resource.type === 'Microsoft.Synapse/workspaces/integrationRuntimes')
                ) {
                    // example: "[concat(parameters('factoryName'), '/', parameters('integrationRuntime_selfhoted-ir1'))]"
                    const parts = resource.name.split(',');

                    const irNameParameter = parts[parts.length - 1];
                    const idxOfName =
                        irNameParameter.indexOf(integrationRuntimeParamPrefix) + integrationRuntimeParamPrefix.length;
                    name = irNameParameter.substring(idxOfName, irNameParameter.length - 4);
                } else if (
                    resource.type === 'Microsoft.DataFactory/factories' ||
                    resource.type === Constants.WORKSPACE_FULLRESOURCE_TYPE
                ) {
                    // case of factory properties parameterization, just set the factory properties prefix.
                    name = ArmTemplateGeneratorService.factoryResourcePrefix;
                } else {
                    // example: "[concat(parameters('factoryName'), '/trigger1')]"
                    const parts = resource.name.split('/');
                    name = parts[parts.length - 1].substring(0, parts[parts.length - 1].length - 3);
                }
                this.parameterizeResource(name, name, index, resource, definition, template);
            }
        }
    }

    private _addSecureStringParameters(template: IArmTemplate): void {
        template.resources.forEach((resource) => {
            // Parameterize secure strings
            const typeProperties = resource.properties.typeProperties;
            for (const typeProperty in typeProperties) {
                if (
                    typeProperties[typeProperty]?.type &&
                    typeof typeProperties[typeProperty].type === 'string' &&
                    stringsAreEqualCaseInsensitive(typeProperties[typeProperty].type, 'SecureString')
                ) {
                    const parts = resource.name.split('/');
                    const name = parts[parts.length - 1].substring(0, parts[parts.length - 1].length - 3);
                    const paramName = `${name}_${typeProperty}`;
                    const originalValue = typeProperties[typeProperty].value;
                    typeProperties[typeProperty].value = `[${format(parameterSyntax, paramName)}]`;
                    template.parameters[paramName] = {
                        type: 'secureString',
                        metadata: format(stringResources.SecureStringParameter, typeProperty, name),
                        defaultValue:
                            typeof originalValue === 'string' && !originalValue.includes(SharedConsts.maskedCredential)
                                ? originalValue
                                : undefined
                    };
                }
            }
            // handle linked srevice secrets in connectionProperties for RFI connectors
            if (typeProperties?.connectionProperties) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const connectionProperties: any = typeProperties.connectionProperties;
                for (const connectionTypeProperty in connectionProperties) {
                    if (
                        connectionProperties[connectionTypeProperty]?.type &&
                        typeof connectionProperties[connectionTypeProperty].type === 'string' &&
                        stringsAreEqualCaseInsensitive(
                            connectionProperties[connectionTypeProperty].type,
                            'SecureString'
                        )
                    ) {
                        const parts = resource.name.split('/');
                        const name = parts[parts.length - 1].substring(0, parts[parts.length - 1].length - 3);
                        const paramName = `${name}_connectionProperties_${connectionTypeProperty}`;
                        connectionProperties[connectionTypeProperty].value = `[${format(parameterSyntax, paramName)}]`;
                        template.parameters[paramName] = {
                            type: 'secureString',
                            metadata: format(stringResources.SecureStringParameter, connectionTypeProperty, name)
                        };
                    }
                }
            }
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private isPropertyParameterizableObject(object: any): boolean {
        return (
            !!object &&
            typeof object === 'object' &&
            Object.keys(object).length === 2 &&
            typeof object.value !== 'undefined' &&
            (object.type === 'Expression' || object.type === 'SecureString')
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getArmTemplateType(value: any): string {
        if (Array.isArray(value)) {
            return 'array';
        }
        const valueType = typeof value;
        if (valueType === 'boolean') {
            return 'bool';
        } else if (valueType === 'number') {
            return 'int';
        } else if (valueType === 'object') {
            return 'object';
        }

        return 'string';
    }

    private async _convertToARMResourceForPublishing(
        fetchedNamed: Named,
        allNamedList: Named[],
        includeAll?: boolean,
        altContext?: AdapterContext
    ): Promise<IPublishableResource> {
        if (fetchedNamed instanceof Database && fetchedNamed.entities?.length === 0) {
            // populate database's lazy-loaded subresources
            const tables = (
                ((await this._modelService
                    .list(undefined, Metamodel.modelByName('table'))
                    .pipe(timeout({ each: 5, with: () => from([]) }))
                    .toPromise()) as DatabaseEntity[]) || []
            ).filter((artifact: DatabaseEntity) => artifact.parentInfo.resourceName === fetchedNamed.name);
            const relationships = (
                ((await this._modelService
                    .list(undefined, Metamodel.modelByName('relationship'))
                    .pipe(timeout(MODEL_SERVICE_LIST_TIMEOUT))
                    .toPromise()) as DatabaseEntity[]) || []
            ).filter((artifact: DatabaseEntity) => artifact.parentInfo.resourceName === fetchedNamed.name);
            await this._modelService.fetchAll([...tables, ...relationships]);
            fetchedNamed.entities = [...tables, ...relationships];
        }
        const context = altContext ? altContext : AdapterContext.defaultContextOf(this._contextData);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const resource: any = await ModelUtilties.toResources(context, fetchedNamed, false, true);
        const dependencies = Walker.directConceptDependencies(this._modelService.modelAttributeContext, fetchedNamed);
        const dependenciesForResource: IDependency[] = [];

        for (const dependency of dependencies) {
            const dependencyConcept = Metamodel.modelOf(dependency).concept;
            if (dependencyConcept) {
                if (!includeAll && stringsAreEqualCaseInsensitive(dependencyConcept.name, 'integrationRuntime')) {
                    continue; // Named doesn't depend on IR.
                }

                if (
                    allNamedList.findIndex(
                        (value) =>
                            value.name === dependency.name &&
                            Metamodel.modelOf(value).name === Metamodel.modelOf(dependency).name
                    ) > -1
                ) {
                    dependenciesForResource.push({
                        resourceName: context.identityProvider.identity(dependency),
                        conceptType: resolveResourceType(dependencyConcept.name)
                    });
                }
            }
        }
        const concept = Metamodel.modelOf(fetchedNamed).concept;
        if (concept) {
            const resourceName = context.identityProvider.identity(fetchedNamed);
            const resourceToPublish = resource[concept.name];
            if (!resourceToPublish.properties) {
                resourceToPublish.properties = {};
            }
            if (resourceToPublish?.properties?.extraTridentInfo && this.getIsExportingTridentPipeline()) {
                delete resourceToPublish.properties.extraTridentInfo;
            }
            const conceptType = resolveResourceType(concept.name) as Constants.ResourceType;

            const publishableResource: IPublishableResource = {
                // TODO - ans - Remove Identity from the resource.
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                identity: this._identityService.identity(fetchedNamed)!,
                resourceName,
                resourceProperties: resourceToPublish.properties,
                conceptType,
                resourceType: PublisherUtils.getResourceType(fetchedNamed),
                dependencies: dependenciesForResource,
                location: resourceToPublish.location
            };

            const isFactory = conceptType === 'factory' || conceptType === 'workspace';
            if (isFactory && resourceToPublish.identity) {
                publishableResource.factoryIdentity = resourceToPublish.identity;
            }

            return publishableResource;
        }
        throw new Error('Only concept models can be published ');
    }

    private _addToPendingFile(fileArray: IFile[], armTemplate: {}, fileName: string): void {
        fileArray.push({
            payload: JSON.stringify(armTemplate, null, 4),
            fileName
        });
    }

    private _excludeTriggersFromResources(resources: Named[]): Named[] {
        return resources.filter((resource) => Metamodel.modelOf(resource).name !== Trigger.MODEL.name);
    }

    // Todo, I want to bring this back with publish config. Let's keep it here first
    // private static _addAkvParameters(template: IArmTemplate): void {
    //     template.resources.forEach((resource) => {
    //         // Parameterize secure strings
    //         const typeProperties = resource.properties.typeProperties;
    //         for (const typeProperty in typeProperties) {
    //             if (typeProperties[typeProperty]?.type
    //                 && stringsAreEqualCaseInsensitive(typeProperties[typeProperty].type, 'AzureKeyVaultSecret')) {
    //                 const parts = resource.name.split('/');
    //                 const name = parts[parts.length - 1].substring(0, parts[parts.length - 1].length - 3);
    //                 const paramName = `${name}_${typeProperty}_secretName`;
    //                 // tslint:disable-next-line:no-any
    //                 const secretName = (typeProperties[typeProperty] as any)['secretName'];
    //                 // tslint:disable-next-line:no-any
    //                 (typeProperties[typeProperty] as any)['secretName'] = `[${format(parameterSyntax, paramName)}]`;
    //                 template.parameters[paramName] = {
    //                     type: 'string',
    //                     defaultValue: secretName
    //                 };
    //             }
    //         }
    //     });
    // }
}
